<template>
  <div>
    <!-- Page content -->
    <v-card style="border-radius: 10px">
      <v-card-title style="color: #595959">
        <v-row>
          <v-col class="py-2">{{ $t("title.qrManagement") }}</v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <!-- Search Section -->
        <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
          <form @submit.prevent="handleSubmit(searchUsers)">
            <v-row align="center">
              <v-col cols="12" md="9">
                <ValidationProvider name="Username" rules="required" v-slot="{ errors }">
                  <v-combobox
                    v-model="usernames"
                    :label="$t('labels.usernames')"
                    :hint="$t('labels.usernamesHint')"
                    persistent-hint
                    multiple
                    chips
                    clearable
                    deletable-chips
                    :error-messages="errors[0]"
                    :counter="10"
                    @keydown.enter.prevent="addUsername"
                    @change="onUsernamesChange"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 10"
                        small
                        close
                        dark
                        color="info"
                        @click:close="removeUsername(index)"
                      >
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="3" align="end">
                <v-btn
                  small
                  elevation="0"
                  outlined
                  color="info"
                  type="submit"
                  :loading="loading.search"
                  :disabled="usernames.length === 0"
                >
                  <v-icon color="info" left small>mdi-magnify</v-icon>
                  {{ $t('buttons.search') }}
                </v-btn>
              </v-col>
            </v-row>
          </form>
        </ValidationObserver>

        <!-- User Results Table -->
        <v-row v-if="users.length > 0" class="mt-2">
          <v-col cols="12">
            <v-row align="center" justify="space-between" class="mb-3">
              <v-col cols="6">
                <span style="color: #595959; font-weight: 500;">
                  {{ $t('title.userList') }} ({{ users.length }})
                </span>
              </v-col>
              <v-col cols="6" align="end">
                <v-btn
                  small
                  elevation="0"
                  outlined
                  color="success"
                  :disabled="selectedUsers.length === 0"
                  :loading="loading.download"
                  @click="downloadQRCodes"
                >
                  <v-icon left small>mdi-qrcode-scan</v-icon>
                  {{ $t('buttons.downloadQR') }}
                  <span v-if="selectedUsers.length > 0" class="ml-1">
                    ({{ selectedUsers.length }})
                  </span>
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              v-model="selectedUsers"
              :headers="headers"
              :items="users"
              :loading="loading.table"
              item-key="sicil"
              show-select
              :no-data-text="$t('description.noList')"
            >
              <template v-slot:[`item.sicil`]="{ item }">
                <div style="font-weight: bold;">{{ item.sicil }}</div>
              </template>

              <template v-slot:[`item.unvan`]="{ item }">
                <div class="text-truncate" style="max-width: 250px;">
                  {{ item.unvan || '-' }}
                </div>
              </template>

              <template v-slot:[`item.sirketAdi`]="{ item }">
                <div class="text-truncate" style="max-width: 250px;">
                  {{ item.sirketAdi || '-' }}
                </div>
              </template>

              <template v-slot:[`item.email`]="{ item }">
                {{ item.email || '-' }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="mr-2"
                      @click="downloadSingleQR(item)"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="item.downloading"
                      color="primary"
                    >
                      mdi-qrcode-scan
                    </v-icon>
                  </template>
                  <span>{{ $t('labels.downloadQR') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-else-if="searched && users.length === 0" class="mt-4">
          <v-col cols="12">
            <v-alert type="info" outlined text>
              <v-row align="center">
                <v-col class="grow">
                  {{ $t('description.noUserFound') }}
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Info Box -->
        <v-row v-if="!searched" class="mt-4">
          <v-col cols="12">
            <v-alert color="info" outlined text>
              <v-row align="center">
                <v-col class="grow">
                  <div style="font-weight: 500;">{{ $t('labels.qrInfo') }}</div>
                  <div class="mt-2">
                    • {{ $t('labels.qrInfo1') }}<br>
                    • {{ $t('labels.qrInfo2') }}<br>
                    • {{ $t('labels.qrInfo3') }}
                  </div>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import QRService from "../services/QRService";

export default {
  name: "QRManagement",
  data() {
    return {
      usernames: [],
      users: [],
      selectedUsers: [],
      searched: false,
      loading: {
        search: false,
        table: false,
        download: false
      },
      headers: [
        {
          text: this.$t("labels.sicilNumber"),
          value: "sicil",
          sortable: true,
          align: "start"
        },
        {
          text: this.$t("labels.nameSurname"),
          value: "adSoyad",
          sortable: true
        },
        {
          text: this.$t("labels.userEmail"),
          value: "email",
          sortable: false
        },
        {
          text: this.$t("labels.header"),
          value: "unvan",
          sortable: false
        },
        {
          text: this.$t("labels.company"),
          value: "sirketAdi",
          sortable: false
        },
        {
          text: this.$t("title.tableHeaderActions"),
          value: "actions",
          sortable: false,
          align: "center"
        }
      ]
    };
  },
  methods: {
    addUsername(event) {
      const value = event.target.value.trim();
      if (value && this.usernames.length < 10 && !this.usernames.includes(value)) {
        this.usernames.push(value);
      }
      event.target.value = '';
    },
    
    async removeUsername(index) {
      this.usernames.splice(index, 1);
      
      if (this.searched) {
        if (this.usernames.length === 0) {
          this.users = [];
          this.selectedUsers = [];
          this.searched = false;
        } else {
          await this.updateTable();
        }
      }
    },

    async onUsernamesChange() {
      if (this.searched && this.usernames.length > 0) {
        await this.updateTable();
      }
    },

    async searchUsers() {
      if (this.usernames.length === 0 || this.usernames.length > 10) {
        this.showWarning(this.$t("description.invalidUsernameCount"));
        return;
      }

      this.loading.search = true;
      this.loading.table = true;
      this.$store.state.loadingStatus = true;
      this.users = [];
      this.selectedUsers = [];

      try {
        const response = await QRService.getQRUsers(this.usernames.join(','));
        
        if (response.data?.Result && response.data.Result.length > 0) {
          this.users = response.data.Result.map(user => ({
            ...user,
            downloading: false
          }));
          this.searched = true;
        } else {
          this.users = [];
          this.searched = true;
          this.showInfo(this.$t("description.noUserFound"));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.showError(error);
        this.searched = true;
      } finally {
        this.loading.search = false;
        this.loading.table = false;
        this.$store.state.loadingStatus = false;
      }
    },

    async updateTable() {
      if (this.usernames.length === 0) {
        this.users = [];
        this.selectedUsers = [];
        this.searched = false;
        return;
      }

      this.loading.table = true;
      this.$store.state.loadingStatus = true;

      try {
        const response = await QRService.getQRUsers(this.usernames.join(','));
        
        if (response.data?.Result && response.data.Result.length > 0) {
          const newUsers = response.data.Result.map(user => ({
            ...user,
            downloading: false
          }));
          
          // Update table by merging with existing data
          const existingSicils = this.users.map(u => u.sicil);
          const newSicils = newUsers.map(u => u.sicil);
          
          // Remove users not in new list
          this.users = this.users.filter(u => newSicils.includes(u.sicil));
          
          // Add new users
          newUsers.forEach(newUser => {
            if (!existingSicils.includes(newUser.sicil)) {
              this.users.push(newUser);
            }
          });
          
          // Update selected users
          this.selectedUsers = this.selectedUsers.filter(u => 
            this.users.some(user => user.sicil === u.sicil)
          );
        } else {
          this.users = [];
          this.selectedUsers = [];
        }
      } catch (error) {
        console.error("Error updating users:", error);
        this.showError(error);
      } finally {
        this.loading.table = false;
        this.$store.state.loadingStatus = false;
      }
    },

    async downloadSingleQR(user) {
      this.$set(user, 'downloading', true);
      this.$store.state.loadingStatus = true;
      
      try {
        const response = await QRService.createQR([
  {
    Username: user.sicil,
    Company: user.sirket
  }
]);
        this.downloadBlob(response.data, `${user.sicil}-${user.sirket}.png`);
        this.showSuccess(this.$t("messages.qrDownloadSuccess"));
      } catch (error) {
        console.error("Error downloading QR code:", error);
        this.showError(error);
      } finally {
        this.$set(user, 'downloading', false);
        this.$store.state.loadingStatus = false;
      }
    },

    async downloadQRCodes() {
      if (this.selectedUsers.length === 0) {
        this.showWarning(this.$t("description.selectUserFirst"));
        return;
      }

      this.loading.download = true;
      this.$store.state.loadingStatus = true;

      try {
        const usernames = this.selectedUsers.map(u => ({Username: u.sicil, Company: u.sirket}));
        const response = await QRService.createQR(usernames);
        const fileName = this.selectedUsers.length > 1 ?  `QR_Codes_${new Date().getTime()}.zip` : `${usernames[0].Username}-${usernames[0].Company}.png`

        // Download as single file or handle multiple files
        this.downloadBlob(response.data, fileName);
        
        this.showSuccess(
          this.$t("messages.qrDownloadSuccessMultiple", { count: this.selectedUsers.length })
        );
      } catch (error) {
        console.error("Error downloading QR codes:", error);
        this.showError(error);
      } finally {
        this.loading.download = false;
        this.$store.state.loadingStatus = false;
      }
    },

    downloadBlob(blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

